import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_TYPES = [
  "General Inquiry",
  "Partnership",
  "Contract Vehicles",
  "Careers",
  "Media & Press",
  "Other",
];

const empty = { request_type: "", first_name: "", last_name: "", email: "", organization: "", message: "" };

const inputCls =
  "bg-transparent border-black/20 text-zinc-950 placeholder:text-zinc-400 focus-visible:border-[#0891B2] focus-visible:ring-1 focus-visible:ring-[#0891B2] rounded-none h-12";

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.request_type || !form.first_name || !form.last_name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Request received — our team will reach out shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 px-6 md:px-12 border-t border-black/10">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 60, damping: 18 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#0891B2] mb-4">Contact Us</p>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight text-zinc-950">
            Start the conversation
          </h2>
          <p className="mt-6 text-[19px] leading-relaxed text-zinc-600 max-w-md">
            Contact us with your questions and feedback using the request form.
            A member of our team will respond promptly.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 60, damping: 18, delay: 0.1 }}
          className="lg:col-span-7 bg-black/[0.03] backdrop-blur-xl border border-black/10 p-8 md:p-12 flex flex-col gap-6"
        >
          <div>
            <label className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Type of Request *
            </label>
            <Select value={form.request_type} onValueChange={(v) => setForm((f) => ({ ...f, request_type: v }))}>
              <SelectTrigger data-testid="request-type-select" className={inputCls}>
                <SelectValue placeholder="Select a request type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-black/10 text-zinc-950 rounded-none">
                {REQUEST_TYPES.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`request-type-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">First Name *</label>
              <Input data-testid="first-name-input" value={form.first_name} onChange={set("first_name")} className={inputCls} placeholder="Jane" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Last Name *</label>
              <Input data-testid="last-name-input" value={form.last_name} onChange={set("last_name")} className={inputCls} placeholder="Doe" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Email *</label>
              <Input data-testid="email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@example.com" />
            </div>
            <div>
              <label className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Organization</label>
              <Input data-testid="organization-input" value={form.organization} onChange={set("organization")} className={inputCls} placeholder="Agency or company" />
            </div>
          </div>

          <div>
            <label className="block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Message *</label>
            <Textarea
              data-testid="message-textarea"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} h-auto min-h-[140px] py-3`}
              placeholder="How can we help?"
            />
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={sending}
            className="self-start bg-[#00E5FF] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#33EAFF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? "Sending..." : "Submit Request"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
